package com.milta.core.auth

import com.milta.BuildKonfig
import com.milta.feature.auth.domain.entity.OAuthProvider
import kotlinx.browser.document
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.withTimeoutOrNull
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi
import kotlin.js.json
import kotlin.time.Duration.Companion.seconds
import org.w3c.dom.HTMLScriptElement

/**
 * Web (Kotlin/JS) OAuth sign-in.
 *
 * Google: Google Identity Services (GIS) JS SDK — `google.accounts.id.prompt()` triggered from
 * inside the "Continue with Google" button's click handler (still works as a user-gesture-backed
 * prompt under FedCM; the browser itself renders the account chooser). Returns an ID token via a
 * one-shot JS callback bridged to a [CompletableDeferred]. firstName/lastName/email are read
 * directly from the (unverified client-side, re-checked server-side) ID token claims — the GIS
 * SDK doesn't surface them separately the way Android's Credential Manager does.
 *
 * Apple: not wired on web — the backend's `/auth/oauth/apple/callback` only redirects to the
 * mobile deep link scheme (`miltaapp://...`) today, which a browser tab can't follow. Left
 * unimplemented on purpose (decision 2026-08-15, see `docs/TODO.md`).
 */
class JsOAuthSignInClient : OAuthSignInClient {

    override suspend fun signIn(provider: OAuthProvider): OAuthSignInResult = when (provider) {
        OAuthProvider.GOOGLE -> signInWithGoogle()
        OAuthProvider.APPLE -> throw UnsupportedOperationException("Apple sign-in isn't wired on web yet")
    }

    private suspend fun signInWithGoogle(): OAuthSignInResult {
        ensureGsiScript()

        val deferred = CompletableDeferred<String>()
        val google: dynamic = js("window").google
        google.accounts.id.initialize(
            json(
                "client_id" to BuildKonfig.GOOGLE_WEB_CLIENT_ID,
                "callback" to { response: dynamic -> deferred.complete(response.credential as String) },
                "use_fedcm_for_prompt" to true,
            ),
        )
        // This moment-notification listener is NOT a reliable fail-fast signal: under FedCM (which
        // `use_fedcm_for_prompt` opts into above), Google's own GIS library logs that
        // isNotDisplayed()/isSkippedMoment() "may stop functioning" — confirmed in practice, the
        // callback never fires at all when the One Tap UI silently declines to show (no active
        // Google session in this browser profile, prior dismiss cooldown...). Kept only as a
        // best-effort early exit/diagnostic log when it DOES fire; the real recovery path is the
        // short timeout below.
        google.accounts.id.prompt(fun(notification: dynamic) {
            if (deferred.isCompleted) return
            val notDisplayed: Boolean = notification.isNotDisplayed()
            val skipped: Boolean = notification.isSkippedMoment()
            if (notDisplayed || skipped) {
                val reason: String? = if (notDisplayed) notification.getNotDisplayedReason() else notification.getSkippedReason()
                println("Google One Tap did not display (reason=$reason)")
                deferred.completeExceptionally(IllegalStateException("Google sign-in prompt was not displayed (reason=$reason)"))
            }
        })

        // Short timeout, not the moment-notification above, is the actual failure signal in
        // practice (see comment above) — a real user facing a genuinely rendered Google account
        // chooser has more than enough time to pick an account within this window; past it, the
        // prompt silently never displayed at all.
        val idToken = withTimeoutOrNull(15.seconds) { deferred.await() }
            ?: throw IllegalStateException("Google sign-in timed out or was dismissed")

        val claims = decodeIdTokenClaims(idToken)
        return OAuthSignInResult(
            idToken = idToken,
            firstName = claims?.get("given_name")?.jsonPrimitive?.contentOrNull,
            lastName = claims?.get("family_name")?.jsonPrimitive?.contentOrNull,
            email = claims?.get("email")?.jsonPrimitive?.contentOrNull,
        )
    }

    private suspend fun ensureGsiScript() {
        val existing: dynamic = js("window").google
        if (existing != null && existing.accounts != null) return

        val deferred = CompletableDeferred<Unit>()
        val script = document.createElement("script") as HTMLScriptElement
        script.src = "https://accounts.google.com/gsi/client"
        script.onload = { deferred.complete(Unit) }
        document.head?.appendChild(script)
        deferred.await()
    }

    @OptIn(ExperimentalEncodingApi::class)
    private fun decodeIdTokenClaims(idToken: String): JsonObject? {
        val payload = idToken.split(".").getOrNull(1) ?: return null
        val padded = payload + "=".repeat((4 - payload.length % 4) % 4)
        return runCatching {
            Json.parseToJsonElement(Base64.UrlSafe.decode(padded).decodeToString()).jsonObject
        }.getOrNull()
    }
}
